// Datos de "Ventas: reportado por el cajero vs sistema" y del resumen de compras de un cierre.
// Una sola fuente para la pantalla (CierreDetalleModal) y el PDF del cierre: mismos valores, reglas y clasificaciones.
// No recalcula el cuadre: usa los totales que guarda el servidor.
import { toMoney, sumarDinero, redondearDinero } from '../../utils/numbers';
import { formatDate } from '../../utils/dates';
import { resultadoCierre } from './estadoCierre';

const n = (v) => Number(v) || 0;

// Las categorías usan el tipo y la forma de pago guardados en la compra.
// Cada compra entra una sola vez; solo Contado + Efectivo representa salida de caja.
export const resumirCompras = (compras) => compras.reduce((resumen, compra) => {
  const monto = redondearDinero(Number(compra.total) || 0);
  const categoria = compra.tipo === 'Crédito' ? 'credito'
    : compra.tipo === 'Contado' && compra.formaPago === 'Efectivo' ? 'efectivo' : 'otros';
  resumen[categoria] = sumarDinero(resumen[categoria], monto);
  resumen.total = sumarDinero(resumen.total, monto);
  return resumen;
}, { efectivo: 0, credito: 0, otros: 0, total: 0 });

export const formaPagoCompra = (k) => (k.formaPago === 'Dueña' ? 'Pagada por la dueña (fuera de caja)' : k.formaPago || 'Por pagar');
export const cantidadItem = (i) => Number(i.cantidad).toLocaleString('es-GT', { maximumFractionDigits: 3 });
// Precio unitario calculado (total ÷ cantidad): solo para análisis de Administración.
export const unitarioItem = (i) => (Number(i.cantidad) > 0 ? toMoney(Math.round((Number(i.subtotal) / Number(i.cantidad)) * 100) / 100) : '—');

/** Qué pasó con el fondo dejado en caja (nota de trazabilidad, no otra fila contable). null si no aplica. */
export const notaFondo = (c, siguiente) => {
  const t = c?.totales || {};
  if (!t.modoFondo || !siguiente) return null;
  const turno = `${formatDate(siguiente.fecha)} · ${siguiente.usuario}`;
  return Math.abs(n(siguiente.efectivoInicial) - n(t.dejaEnCaja)) < 0.005
    ? { alerta: false, texto: `Los ${toMoney(t.dejaEnCaja)} dejados en caja fueron la apertura del siguiente turno (${turno}).` }
    : { alerta: true, texto: `El siguiente turno (${turno}) abrió con ${toMoney(siguiente.efectivoInicial)}: no coincide con los ${toMoney(t.dejaEnCaja)} dejados en caja.` };
};

/** Valores guardados del sistema (POS) del cierre, en el mismo formato que los campos de la pantalla. */
export const sistemaGuardado = (c) => {
  const s = c?.cierre?.[0] || {};
  return { efectivo: s.efectivo ?? '', tarjeta: s.tarjeta ?? '', pedidosYa: s.pedidosYa ?? '' };
};

/**
 * @param c          cierre (detalle.cierre)
 * @param servicios  servicios activos de la sucursal ({ pos, pedidosYa })
 * @param sistema    datos del sistema que se muestran (por defecto, los guardados)
 */
export function cuadreVista(c, { servicios = {}, sistema = sistemaGuardado(c) } = {}) {
  const t = c?.totales || {}, rep = c?.arqueo?.[0] || {};
  // El efectivo conserva el significado original guardado en cada formato de cierre.
  const conteoCaja = rep.modoEfectivo === 'contado';
  const ventasIndependientes = (rep.modoEfectivo === 'ventas' || (!rep.modoEfectivo && rep.efectivoContado != null))
    && t.ventasEfectivoReportadas != null;
  const conteoHistorico = !rep.modoEfectivo && rep.efectivoContado == null && rep.efectivoManual != null;
  const efectivoContado = t.efectivoContadoCaja ?? t.efectivoContado ?? (conteoHistorico ? n(rep.efectivoManual) : null);
  const subtotalCajero = efectivoContado == null ? null : sumarDinero(efectivoContado, n(t.salidasEfectivo));
  const subtotalSistema = t.ventasEfectivo == null ? null
    : sumarDinero(n(t.efectivoInicial), n(t.ventasEfectivo), n(t.cobrosSucursalesEfectivo));
  const resultado = resultadoCierre(c);
  const resultadoTexto = subtotalCajero == null ? 'Sin conteo físico' : subtotalSistema == null || t.diferenciaCaja == null
    ? 'Pendiente de datos del sistema' : resultado.tipo === 'Cuadrado' ? 'Cuadrado' : `${resultado.tipo} ${toMoney(Math.abs(t.diferenciaCaja))}`;
  const medios = [
    { label: 'Tarjeta', k: 'tarjeta', cajero: n(rep.tarjeta), diferencia: t.diferenciaTarjeta, pct: t.comisionTarjetaPct, comision: t.comisionTarjeta, neto: t.netoTarjeta },
    { label: 'PedidosYa', k: 'pedidosYa', cajero: n(rep.pedidosYa), diferencia: t.diferenciaPedidosYa, pct: t.comisionPedidosYaPct, comision: t.comisionPedidosYa, neto: t.netoPedidosYa },
  ] // Solo los servicios activos de la sucursal (o si ya tienen montos registrados).
    .filter((m) => (m.k === 'tarjeta' ? servicios.pos : servicios.pedidosYa) || m.cajero > 0 || n(sistema[m.k]) > 0)
    .map((m) => ({ ...m, sistema: sistema[m.k] }));
  return {
    conteoCaja, ventasIndependientes, conteoHistorico, efectivoContado, subtotalCajero, subtotalSistema, resultado, resultadoTexto, medios,
    apertura: t.efectivoInicial, ventasEfectivoSistema: sistema.efectivo, cobros: n(t.cobrosSucursalesEfectivo), salidas: t.salidasEfectivo,
    ventasEfectivoReportadas: t.ventasEfectivoReportadas, faltantePagado: n(c?.faltantePagado), ventasTotales: t.ventasTotales,
    netoSobre: t.comisionTarjetaPct == null ? null : t.netoSobreSistema ? 'el dato del sistema' : 'lo reportado por el cajero',
  };
}
